import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Award, MapPin, Leaf, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function MunicipalServices() {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = `${t('municipal.title')} | Orange Onderhoudsbedrijf`;
  }, []);

  const benefits = [
    { icon: FileText, title: t('municipal.benefit1Title'), desc: t('municipal.benefit1Desc') },
    { icon: Award, title: t('municipal.benefit2Title'), desc: t('municipal.benefit2Desc') },
    { icon: MapPin, title: t('municipal.benefit3Title'), desc: t('municipal.benefit3Desc') },
    { icon: Leaf, title: t('municipal.benefit4Title'), desc: t('municipal.benefit4Desc') },
  ];

  const steps = [
    { num: '01', title: t('municipal.step1'), desc: t('municipal.step1Desc') },
    { num: '02', title: t('municipal.step2'), desc: t('municipal.step2Desc') },
    { num: '03', title: t('municipal.step3'), desc: t('municipal.step3Desc') },
    { num: '04', title: t('municipal.step4'), desc: t('municipal.step4Desc') },
  ];

  return (
    <div className="bg-[#F8FAFC] pt-20">
      {/* Header */}
      <section data-testid="municipal-header" className="bg-white border-b border-slate-100 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Gemeente</span>
          <h1 className="text-5xl md:text-6xl font-black text-[#0F172A] mt-3 mb-5">{t('municipal.title')}</h1>
          <p className="text-xl text-[#64748B] leading-relaxed">{t('municipal.subtitle')}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-lg text-[#334155] leading-relaxed mb-6">{t('municipal.intro')}</p>
            <ul className="space-y-3">
              {['KvK geregistreerd', 'VAR / aanbestedingservaring', 'Verzekerd & gecertificeerd', 'Lokale partner'].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle size={18} className="text-[#FF5F00] flex-shrink-0" />
                  <span className="text-[#334155] font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80"
              alt="Professional maintenance team"
              className="rounded-2xl object-cover w-full h-80"
            />
            <div className="absolute -bottom-4 -left-4 bg-[#FF5F00] text-white p-6 rounded-2xl shadow-xl">
              <p className="text-3xl font-black">10+</p>
              <p className="text-orange-100 text-sm font-semibold">Jaar gemeentelijke ervaring</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="municipal-benefits" className="bg-white px-6 md:px-12 lg:px-24 py-16">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Orange</span>
          <h2 className="text-4xl font-bold text-[#0F172A] mt-3">{t('municipal.benefitsTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <div key={i} data-testid={`benefit-${i}`} className="bg-[#F8FAFC] rounded-2xl p-7 border border-slate-100 hover:border-orange-200 transition-colors text-center">
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <b.icon size={28} strokeWidth={1.5} className="text-[#FF5F00]" />
              </div>
              <h3 className="font-bold text-[#0F172A] text-lg mb-2">{b.title}</h3>
              <p className="text-[#64748B] text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section data-testid="municipal-process" className="px-6 md:px-12 lg:px-24 py-16">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5F00]">Process</span>
          <h2 className="text-4xl font-bold text-[#0F172A] mt-3">{t('municipal.processTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div key={i} data-testid={`step-${i}`} className="relative">
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-orange-200 to-transparent -z-10" />
              )}
              <div className="bg-white border border-slate-100 rounded-2xl p-7 hover:border-orange-200 transition-colors">
                <div className="text-4xl font-black text-orange-100 mb-4">{step.num}</div>
                <div className="w-8 h-1 bg-[#FF5F00] rounded mb-4" />
                <h3 className="font-bold text-[#0F172A] text-lg mb-2">{step.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <div className="bg-gradient-to-br from-[#FF5F00] to-[#EA580C] rounded-3xl p-10 md:p-16 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">{t('municipal.ctaTitle')}</h2>
          <p className="text-orange-100 text-lg mb-8 max-w-xl mx-auto">{t('municipal.ctaSubtitle')}</p>
          <Link
            to="/contact"
            data-testid="municipal-cta"
            className="inline-flex items-center gap-2 bg-white text-[#FF5F00] px-10 py-4 rounded-xl font-black text-lg hover:bg-orange-50 transition-all shadow-lg"
          >
            {t('municipal.ctaButton')} <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
